import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import {
  isNotificationSupported,
  hasNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  getSubscription,
  subscribeUser,
  unsubscribeUser,
  testLocalNotification,
  isMobileDevice,
  isOnline,
} from '../services/notificationsService';
import { horariosOficiaisService } from '../services/horariosOficiaisService';

export function useNotifications() {
  const { enqueueSnackbar } = useSnackbar();
  const [isSupported, setIsSupported] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Verificar estado inicial
  useEffect(() => {
    const checkStatus = async () => {
      setIsSupported(isNotificationSupported());
      setHasPermission(hasNotificationPermission());

      const subscription = await getSubscription();
      setIsSubscribed(!!subscription);
    };

    checkStatus();
  }, []);

  // Habilitar notificações
  const enableNotifications = useCallback(async () => {
    setIsLoading(true);

    try {
      console.log('[useNotifications] Iniciando habilitação de notificações...');

      // Verificar conectividade (importante para mobile)
      if (!isOnline()) {
        throw new Error('Sem conexão com a internet. Conecte-se e tente novamente.');
      }

      const isMobile = isMobileDevice();
      console.log('[useNotifications] Dispositivo:', isMobile ? 'Mobile' : 'Desktop');

      // 1. Solicitar permissão
      console.log('[useNotifications] Solicitando permissão...');
      const granted = await requestNotificationPermission();
      if (!granted) {
        throw new Error('Permissão de notificação negada');
      }

      setHasPermission(true);
      console.log('[useNotifications] Permissão concedida');

      // 2. Registrar Service Worker
      console.log('[useNotifications] Registrando Service Worker...');
      await registerServiceWorker();
      console.log('[useNotifications] Service Worker registrado');

      // 3. Criar subscription
      console.log('[useNotifications] Criando subscription...');
      const subscription = await subscribeUser();
      console.log('[useNotifications] Subscription criada');

      // 4. Enviar subscription para o backend
      console.log('[useNotifications] Enviando subscription para backend...');
      const subscriptionData = subscription.toJSON();
      await horariosOficiaisService.registrarNotificacao({
        endpoint: subscriptionData.endpoint!,
        keys: {
          p256dh: subscriptionData.keys!.p256dh,
          auth: subscriptionData.keys!.auth,
        },
      });

      setIsSubscribed(true);
      console.log('[useNotifications] Notificações habilitadas com sucesso');

      enqueueSnackbar('Notificações habilitadas com sucesso! 🔔', {
        variant: 'success',
      });

      return true;
    } catch (error: any) {
      console.error('[useNotifications] Erro ao habilitar notificações:', error);

      let errorMessage = 'Erro ao habilitar notificações';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Adicionar dica para mobile se for erro de push service
      if (isMobileDevice() && errorMessage.includes('push service')) {
        errorMessage = 'Erro no serviço de push. Tente: 1) Verificar conexão, 2) Atualizar navegador, 3) Limpar cache.';
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  // Desabilitar notificações
  const disableNotifications = useCallback(async () => {
    setIsLoading(true);

    try {
      // 1. Cancelar subscription local
      await unsubscribeUser();

      // 2. Desativar no backend
      await horariosOficiaisService.desativarNotificacao();

      setIsSubscribed(false);

      enqueueSnackbar('Notificações desabilitadas', {
        variant: 'info',
      });

      return true;
    } catch (error: any) {
      console.error('[useNotifications] Erro ao desabilitar notificações:', error);

      enqueueSnackbar(
        error.message || 'Erro ao desabilitar notificações',
        { variant: 'error' }
      );

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  // Testar notificação
  const testNotification = useCallback(async () => {
    try {
      if (!isSubscribed) {
        enqueueSnackbar('Habilite as notificações primeiro', {
          variant: 'warning',
        });
        return;
      }

      await testLocalNotification();

      enqueueSnackbar('Notificação de teste enviada!', {
        variant: 'success',
      });
    } catch (error: any) {
      console.error('[useNotifications] Erro ao testar notificação:', error);

      enqueueSnackbar(
        error.message || 'Erro ao enviar notificação de teste',
        { variant: 'error' }
      );
    }
  }, [isSubscribed, enqueueSnackbar]);

  return {
    isSupported,
    hasPermission,
    isSubscribed,
    isLoading,
    enableNotifications,
    disableNotifications,
    testNotification,
  };
}
